// src/pages/theme-preview.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { themes } from "@/theme/themes";
import { loadTheme } from "@/theme/themeLoader";

/* Components (match your exports) */
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { About } from "@/components/About";
import { Gallery } from "@/components/Gallery";
import { Awards } from "@/components/Awards";
import { Pricing } from "@/components/Pricing";
import Contact from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { getFactoryForTheme } from "@/theme/factorySeasonal";
import { resolveProductionTheme, setProductionOverride as globalSetProductionOverride } from "@/theme/productionTheme";	 	
import { useNoIndex } from "@/hooks/useNoIndex";



/* ---------- Types & Constants ---------- */

type ThemeRecord = Record<string, any>;

type HistoryItem = {
  id: string;
  tag: string;
  at: string;        // ISO timestamp
  theme: ThemeRecord;
  kind?: "promotion" | "rollback";
  fromTag?: string;
};

type HistoryGetResponse =
  | { ok: true; items: HistoryItem[]; factory: HistoryItem | null }
  | { ok: false; error: string };

const STORAGE_KEYS = {
  OVERRIDES: "theme_preview_overrides_v1", // local, for non-production preview overrides only
} as const;

// src/pages/theme-preview.tsx  — REPLACE the whole FIELDS array with this

const FIELDS = [
  // ── Global
  { key: "background", label: "Site Background", type: "color" as const, controls: "About, Gallery, Pricing, Contact backgrounds" },
  { key: "fontBody",  label: "Body Text & Active Button Text", type: "color" as const, controls: "Regular text + active button text (except Hero & Awards buttons)" },

  // ── Headings / Subheadings (non-Hero)
  { key: "buttonBg",  label: "Headings & Subheadings (non-Hero)", type: "color" as const, controls: "H1/H2/H3 for About and Gallery outside the Hero" },

  // 🚫 removed: recognitionBtnText

  // ── HERO (Hero-only)
  { key: "heroH1", label: "Hero • H1", type: "color" as const, controls: "Hero H1, Awards H1 and Contact form border" },
  { key: "heroH2", label: "Hero • H2", type: "color" as const, controls: "Hero Subtitle text and Awards H2" },
  { key: "heroBtnBg",            label: "Hero • Button BG", type: "color" as const, controls: "Hero primary/CTA button background" },
  { key: "heroBtnText",          label: "Hero • Button Text", type: "color" as const, controls: "Hero CTA text color" },
  { key: "heroBtnHoverBg",       label: "Hero • Button Hover BG", type: "color" as const, controls: "Hero CTA background on hover" },
  { key: "heroBtnHoverText",     label: "Hero • Button Hover Text", type: "color" as const, controls: "Hero CTA text on hover" },

  // ── About (buttons only)
  { key: "aboutBtnBg",      label: "About • Button BG", type: "color" as const, controls: "About and Cake Pricing button background" },
  { key: "aboutBtnText",    label: "About • Button Text", type: "color" as const, controls: "About and Cake Pricing button text" },
  { key: "aboutBtnHoverBg", label: "About • Button Hover BG", type: "color" as const, controls: "About and Cake Pricing Hover BG" },
  { key: "aboutBtnHoverText", label: "About • Button Hover Text", type: "color" as const, controls: "About and Cake Pricing Hover Text" },
  { key: "aboutCardTint",        label: "About • Closing Card Tint", type: "color" as const, controls: "Background of the closing statement card" },

  // ── Gallery (tabs + bottom CTA)
  { key: "galleryTabBg",         label: "Gallery • Tab BG", type: "color" as const, controls: "Filter tab background" },
  { key: "galleryTabText",       label: "Gallery • Tab Text", type: "color" as const, controls: "Filter tab text" },
  { key: "galleryTabHoverBg",    label: "Gallery • Tab Hover BG", type: "color" as const, controls: "Filter tab hover background" },
  { key: "galleryTabHoverText",  label: "Gallery • Tab Hover Text", type: "color" as const, controls: "Filter tab hover text" },
  { key: "galleryCtaBg",    label: "Gallery • Button BG", type: "color" as const, controls: "Gallery - Bottom button background and Client Review title text" },
  { key: "galleryCtaText",  label: "Gallery • Button Text", type: "color" as const, controls: "Gallery - Bottom button text" },
  { key: "galleryCtaHoverBg", label: "Gallery • Button Hover BG", type: "color" as const, controls: "Gallery - Bottom button hover background" },
  { key: "galleryCtaHoverText", label: "Gallery • Button Hover Text", type: "color" as const, controls: "Gallery - Bottom button hover text" },

  // (Awards tabs use Gallery tab colors)

  // ── Navigation (Header)
  { key: "navBg",                   label: "Nav • Base Background",    type: "color" as const, controls: "Solid/gradient base behind pattern (e.g. #ffffff or linear-gradient(...))" },
  { key: "navPatternImage",         label: "Nav • Pattern Image",      type: "file"  as const, controls: "Upload or paste URL for pattern image (SVG/PNG/JPG). Leave empty for none." },
  { key: "navPatternRemove",        label: "Nav • Remove Pattern",     type: "select" as const, options: ["yes","no"], controls: "Yes = hide pattern layer entirely; No = show the pattern if provided." },
  { key: "navPatternTransparent",   label: "Nav • Pattern Transparent BG", type: "select" as const, options: ["yes","no"], controls: "Yes = pattern layer’s own background is transparent; No = it inherits the base nav color under the pattern." },
  { key: "navPatternSize",          label: "Nav • Pattern Size",       type: "range" as const, min: 1, max: 100, step: 1, controls: "Pattern scale (1–100%). Use the slider for live preview." },
  { key: "navPatternRepeat",        label: "Nav • Pattern Repeat",     type: "select" as const, options: ["repeat","no-repeat","repeat-x","repeat-y","space","round"], controls: "repeat: tile both axes • no-repeat: single image • repeat-x: horizontal only • repeat-y: vertical only • space: spaced tiles • round: tiles scaled to fit evenly" },
  { key: "navPatternOpacity",       label: "Nav • Pattern Opacity",    type: "range"  as const, min: 0, max: 1, step: 0.05, controls: "0.0–1.0 opacity for the pattern layer (slider + numeric)." },
  { key: "navBgOpacity",            label: "Nav • Background Opacity", type: "range", min: 0, max: 1, step: 0.05, controls: "Adjust base background opacity (0–1)" },



  // 🚫 removed: all Pricing button keys (pricingBtnBg/Text/HoverBg/HoverText)
  // 🚫 removed: Contact form panel bg (contactFormBg)

  { key: "contactBtnBg",         label: "Contact • Button BG", type: "color" as const, controls: "Contact buttons background" },
  { key: "contactBtnText",       label: "Contact • Button Text", type: "color" as const, controls: "Contact buttons text" },
  { key: "contactBtnHoverBg",    label: "Contact • Button Hover BG", type: "color" as const, controls: "Contact buttons hover background" },
  { key: "contactBtnHoverText",  label: "Contact • Button Hover Text", type: "color" as const, controls: "Contact buttons hover text" },

  // 🚫 removed: Awards button keys (awardsBtnBg/Text/HoverBg/HoverText)
  // 🚫 removed: Client Reviews button keys + clientName (clientBtn*, clientName)
] as const;

type FieldKey = (typeof FIELDS)[number]["key"];

// Keys we’ll send to the server (mirror server allowlist)
const SERVER_ALLOWED_KEYS = new Set<string>(FIELDS.map(f => f.key));

// Return a copy containing only keys the server accepts (non-empty strings or headerTransparency number-ish)
function filterThemeForServer(obj: any): ThemeRecord {
  const out: ThemeRecord = {};
  if (!obj || typeof obj !== "object") return out;
  for (const [k, v] of Object.entries(obj)) {
    if (!SERVER_ALLOWED_KEYS.has(k)) continue;
    if (k === "headerTransparency") {
      const n = Number(v);
      if (!Number.isNaN(n) && n >= 0 && n <= 1) out[k] = String(n);
      continue;
    }
    if (v != null && String(v).trim() !== "") out[k] = String(v).trim();
  }
  return out;
}


/* ---------- Small helpers ---------- */

const clone = <T,>(obj: T) => JSON.parse(JSON.stringify(obj)) as T;

function readLS<T = any>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
function writeLS(key: string, value: any) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}
function downloadJSON(filename: string, obj: any) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function isHexColor(v: any): v is string {
  return typeof v === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v.trim());
}

function toHslTriplet(input: string): string | null {
  // Accept hex "#rrggbb" | "#rgb" or any CSS color (rgb(), hsl(), var(...))
  // Normalize via computed style to rgb(), then convert to HSL
  try {
    const el = document.createElement("div");
    el.style.color = input;
    document.body.appendChild(el);
    const rgb = getComputedStyle(el).color || "";
    document.body.removeChild(el);
    const m = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!m) return null;
    const [r, g, b] = [parseInt(m[1],10)/255, parseInt(m[2],10)/255, parseInt(m[3],10)/255];
    const max = Math.max(r,g,b), min = Math.min(r,g,b);
    let h = 0, s = 0, l = (max+min)/2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d/(2 - max - min) : d/(max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    // convert to degrees / percent triplet that shadcn expects: "H S% L%"
    const H = Math.round(h * 360);
    const S = Math.round(s * 100);
    const L = Math.round(l * 100);
    return `${H} ${S}% ${L}%`;
  } catch {
    return null;
  }
}


function cssColorToHex(input: string): string | null {
  try {
    if (!input || typeof input !== "string") return null;
    const el = document.createElement("div");
    el.style.color = "";
    el.style.color = input; // let browser resolve var()/rgb()/hsl()/named colors
    document.body.appendChild(el);
    const rgb = getComputedStyle(el).color || "";
    document.body.removeChild(el);

    const m = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!m) return null;
    const [r, g, b] = [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)];
    const toHex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } catch {
    return null;
  }
}
function pickerValue(value: any): string {
  const v = String(value ?? "");
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v.trim())) return v;
  const hex = cssColorToHex(v);
  // If we truly cannot resolve the current color, show a neutral preview swatch,
  // but we try very hard not to fall back to black.
  return hex || "#7f7f7f";
}

// Read a computed CSS variable from :root
function readCssVar(varName: string): string {
  try {
    const cs = getComputedStyle(document.documentElement);
    const v = cs.getPropertyValue(varName);
    return (v || "").trim();
  } catch {
    return "";
  }
}

// Aliases used by components today (first hit wins)

const CSS_VAR_ALIASES: Record<string, string[]> = {
  background: ["--color-background", "--background"],
  fontBody: ["--color-foreground", "--foreground"],
  buttonBg: ["--heading-color"],

  // Hero
  heroH1: ["--hero-h1"],
  heroH2: ["--hero-h2"],
  heroBtnBg: ["--hero-btn-bg"],
  heroBtnText: ["--hero-btn-text"],
  heroBtnHoverBg: ["--hero-btn-hover-bg"],
  heroBtnHoverText: ["--hero-btn-hover-text"],

  // About buttons
  aboutBtnBg: ["--about-btn-bg"],
  aboutBtnText: ["--about-btn-text"],
  aboutBtnHoverBg: ["--about-btn-hover-bg"],
  aboutBtnHoverText: ["--about-btn-hover-text"],
  aboutCardTint: ["--about-card-tint"],

  // Gallery (tabs + CTA) — prefer active/inactive vars actually used by components
  galleryTabBg: [
    "--gallery-tab-active-bg",    // active bg (preferred)
    "--gallery-tab-bg"            // legacy
  ],
  galleryTabText: [
    "--gallery-tab-active-text",  // active text (preferred)
    "--gallery-tab-text"          // legacy
  ],
  galleryTabHoverBg: [
    "--gallery-tab-inactive-bg",  // inactive bg (preferred)
    "--gallery-tab-hover-bg"      // legacy
  ],
  galleryTabHoverText: [
    "--gallery-tab-inactive-text",// inactive text (preferred)
    "--gallery-tab-hover-text"    // legacy
  ],
  galleryCtaBg: ["--gallery-cta-bg", "--primary"],
  galleryCtaText: ["--gallery-cta-text", "--primary-foreground"],
  galleryCtaHoverBg: ["--gallery-cta-hover-bg"],
  galleryCtaHoverText: ["--gallery-cta-hover-text"],

  // Awards (tabs)
  awardsTabActiveBg:     ["--awards-tab-active-bg"],
  awardsTabActiveText:   ["--awards-tab-active-text"],
  awardsTabInactiveBg:   ["--awards-tab-inactive-bg"],
  awardsTabInactiveText: ["--awards-tab-inactive-text"],

  // Navigation (header) — match Header.tsx
  navBg:               ["--nav-base-bg"],
  navBgOpacity:        ["--nav-bg-opacity"],
  navPatternImage:     ["--nav-pattern"],
  navPatternSize:      ["--nav-pattern-size"],
  navPatternRepeat:    ["--nav-pattern-repeat"],
  navPatternOpacity:   ["--nav-pattern-opacity"],

  // Contact (buttons only)
  contactBtnBg: ["--contact-btn-bg"],
  contactBtnText: ["--contact-btn-text"],
  contactBtnHoverBg: ["--contact-btn-hover-bg"],
  contactBtnHoverText: ["--contact-btn-hover-text"],
};

// Very forgiving DOM readers used when the CSS var is missing.
// We intentionally keep selectors generic so it works with your current Hero.
const readStyle = (selector: string, prop: keyof CSSStyleDeclaration): string => {
  const el =
    document.querySelector(selector) ||
    document.querySelector(`#hero ${selector}`) ||
    document.querySelector(`.hero ${selector}`);
  if (!el) return "";
  const cs = getComputedStyle(el as Element);
  return (cs[prop] as unknown as string) || "";
};

// Per-key DOM fallbacks (Hero only for now)
const DOM_FALLBACKS: Record<string, () => string> = {
  heroH1: () => readStyle("h1", "color"),
  heroH2: () => readStyle("h2, .subtitle, [data-hero-subtitle]", "color"),
  heroBtnBg: () =>
    readStyle("button, a.button, .btn-primary", "backgroundColor") || readStyle("button", "borderColor"),
  heroBtnText: () => readStyle("button, a.button, .btn-primary", "color"),
  heroBtnHoverBg: () => "",     // hover is not computable directly; leave empty if not present
  heroBtnHoverText: () => "",
};

function hydrateThemeFromCSS(base: Record<string, any>): Record<string, any> {
  const next = { ...(base || {}) };

  for (const f of FIELDS) {
    const k = f.key as string;

    // keep any explicit value we already have
    const has =
      next[k] !== undefined &&
      next[k] !== null &&
      String(next[k]).trim() !== "";
    if (has) continue;

    // 1) canonical --<key>
    let resolved = readCssVar(`--${k}`);

    // 2) aliases (what your components actually use)
    if (!resolved) {
      const aliases = CSS_VAR_ALIASES[k] || [];
      for (const alias of aliases) {
        resolved = readCssVar(alias);
        if (resolved) break;
      }
    }

    // 3) DOM fallbacks (hero)
    if (!resolved && DOM_FALLBACKS[k]) {
      resolved = DOM_FALLBACKS[k]();
    }

    if (resolved) next[k] = resolved;
  }

  return next;
}


function isValidThemeObject(obj: any) {
  // Accept partial objects; we merge over production and section defaults.
  return !!obj && typeof obj === "object";
}
/** Apply to CSS vars for live preview (with semantic aliases many components use) */
/** Apply to CSS vars for live preview (with semantic aliases many components use) */
function applyThemeVars(theme: ThemeRecord) {
  const root = document.documentElement;
  const set = (name: string, val: any) => {
    if (val === undefined || val === null || val === "") return;
    try { root.style.setProperty(name, String(val)); } catch {}
  };

  // 1) raw keys -> CSS vars (for easy debugging / direct consumption)
  for (const f of FIELDS) set(`--${f.key}`, (theme as any)[f.key]);

  // 2) Global base
  set("--color-background", theme.background);
  set("--color-foreground", theme.fontBody);

  // Headings outside hero
  set("--heading-color", theme.buttonBg);

  // Hero
  set("--hero-title", theme.heroTitle);
  set("--hero-h1", theme.heroH1);
  set("--hero-h2", theme.heroH2);
  set("--hero-btn-bg", theme.heroBtnBg);
  set("--hero-btn-text", theme.heroBtnText);
  set("--hero-btn-hover-bg", theme.heroBtnHoverBg);
  set("--hero-btn-hover-text", theme.heroBtnHoverText);

  // About buttons
  set("--about-btn-bg", theme.aboutBtnBg);
  set("--about-btn-text", theme.aboutBtnText);
  set("--about-btn-hover-bg", theme.aboutBtnHoverBg);
  set("--about-btn-hover-text", theme.aboutBtnHoverText);
  set("--about-card-tint", theme.aboutCardTint);


  // Gallery tabs (ACTIVE/INACTIVE) + CTA
  // Active uses galleryTabBg/Text; Inactive uses galleryTabHoverBg/Text
  set("--gallery-tab-active-bg",  theme.galleryTabBg);
  set("--gallery-tab-active-text",theme.galleryTabText);
  set("--gallery-tab-inactive-bg",theme.galleryTabHoverBg);
  set("--gallery-tab-inactive-text", theme.galleryTabHoverText);

  // Awards tabs mirror Gallery tabs 1:1
  set("--awards-tab-active-bg",     theme.galleryTabBg);
  set("--awards-tab-active-text",   theme.galleryTabText);
  set("--awards-tab-inactive-bg",   theme.galleryTabHoverBg);
  set("--awards-tab-inactive-text", theme.galleryTabHoverText);

  // CTA stays the same
  set("--gallery-cta-bg", theme.galleryCtaBg);
  set("--gallery-cta-text", theme.galleryCtaText);
  set("--gallery-cta-hover-bg", theme.galleryCtaHoverBg);
  set("--gallery-cta-hover-text", theme.galleryCtaHoverText);

  // Navigation (header) — must match Header.tsx usage
  // Base color (solid or gradient)
  set("--nav-base-bg",        theme.navBg);
  // Opacity for the base color (0..1); Header uses color-mix with this var
  if (typeof theme.navBgOpacity === "number") {
    set("--nav-bg-opacity", String(theme.navBgOpacity));
  }

  // Pattern layer — honor Remove + Transparent BG
  {
    const remove = String(theme.navPatternRemove ?? "no").toLowerCase() === "yes";
    const img = theme.navPatternImage ? `url(${String(theme.navPatternImage)})` : "none";
    set("--nav-pattern", remove ? "none" : img);

    const transparent = String(theme.navPatternTransparent ?? "yes").toLowerCase() === "yes";
    // If not transparent, fall back to the same base color you feed the header
    set("--nav-pattern-bg", transparent ? "transparent" : (theme.navBg || "var(--nav-base-bg, #ffffff)"));
  }
  set("--nav-pattern-size",   theme.navPatternSize ? `${String(theme.navPatternSize)}%` : "auto");
  set("--nav-pattern-repeat", theme.navPatternRepeat || "repeat");
  set("--nav-pattern-opacity", String(
    typeof theme.navPatternOpacity === "number" ? theme.navPatternOpacity : 1
  ));


  // 3) shadcn tokens (mostly for Button/typography defaults outside special sections)
  const hsl = (c: any) => {
    try { return toHslTriplet(String(c || "")); } catch { return null; }
  };
  const setHSL = (name: string, color: any) => { const t = hsl(color); if (t) set(name, t); };

  setHSL("--background", theme.background || "#ffffff");
  setHSL("--foreground", theme.fontBody   || "#111111");

  // Default button tokens map to Gallery CTA (NOT awards/client/hero)
  setHSL("--primary",            theme.galleryCtaBg   ?? theme.buttonBg);
  setHSL("--primary-foreground", theme.galleryCtaText ?? theme.fontBody);

  // Optional muted tone from body for 'text-muted-foreground'
  setHSL("--muted-foreground",   theme.fontBody || "#6B7280");

  // 4) immediate body styles
  try {
    if (theme.background) document.body.style.background = String(theme.background);
    if (theme.fontBody)   document.body.style.color      = String(theme.fontBody);
  } catch {}
}

function getVariant(): "light" | "dark" {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}


/** Detect production key present in themes */
function getProductionKey(): "production" | "default" | string {
  if ("production" in themes) return "production";
  if ("default" in themes) return "default";
  const keys = Object.keys(themes || {});
  return keys[0] || "production";
}

// ---------- API base ----------
const API_BASE = ((import.meta as any).env?.VITE_THEME_HISTORY_BASE ?? "").trim();
const THEME_HISTORY_URL =
  API_BASE ? `${API_BASE.replace(/\/$/, "")}/api/theme-history` : "/api/theme-history";


/* ---------- API helpers (server-backed) ---------- */

async function safeJson<T = any>(res: Response): Promise<T | null> {
  try {
    const txt = await res.text();
    if (!txt) return null;
    return JSON.parse(txt) as T;
  } catch {
    return null;
  }
}

async function apiGetHistory(): Promise<{ items: HistoryItem[]; factory: HistoryItem | null }> {
  try {
    const res = await fetch(THEME_HISTORY_URL, { method: "GET" });
    const j = await safeJson<HistoryGetResponse>(res);
    if (!res.ok || !j || (j as any).ok !== true) {
      throw new Error((j as any)?.error || `HTTP ${res.status}`);
    }
    const items = Array.isArray((j as any).items) ? (j as any).items : [];
    const factory = (j as any).factory && typeof (j as any).factory === "object" ? (j as any).factory : null;
    return { items, factory };
  } catch (e) {
    console.error("[ThemePreview] GET /api/theme-history failed:", e);
    return { items: [], factory: null };
  }
}


async function apiPostPromotion(tag: string, theme: ThemeRecord): Promise<boolean> {
  try {
    const res = await fetch(THEME_HISTORY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tag, theme }),
    });
    const j = await safeJson<any>(res);
    if (!res.ok || !(j && j.ok === true)) {
      const msg = j?.error || `HTTP ${res.status}`;
      throw new Error(msg);
    }
    return true;
  } catch (e) {
    console.error("[ThemePreview] POST /api/theme-history failed:", e);
    alert(`Failed to promote: ${(e as Error)?.message || e}`);
    return false;
  }
}

async function apiDeleteHistory(id: string): Promise<boolean> {
  try {
    const res = await fetch(THEME_HISTORY_URL, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    const j = await safeJson<any>(res);
    if (!res.ok || !(j && j.ok === true)) throw new Error(j?.error || `HTTP ${res.status}`);
    return true;
  } catch (e) {
    console.error("[ThemePreview] DELETE /api/theme-history failed:", e);
    alert(`Failed to delete entry: ${(e as Error)?.message || e}`);
    return false;
  }
}

async function apiSeedFactory(theme: ThemeRecord, tag = "Factory Setting"): Promise<boolean> {
  try {
    const res = await fetch(THEME_HISTORY_URL, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: "factory", tag, theme }),
    });
    const j = await safeJson<any>(res);
    return !!(res.ok && j?.ok === true);
  } catch (e) {
    console.error("[ThemePreview] PUT /api/theme-history (factory) failed:", e);
    return false;
  }
}


/* ---------- Main Component ---------- */

function ThemePreview(): JSX.Element {
  useNoIndex();

  const prodKey = getProductionKey();

  const themeKeys = useMemo(() => {
    const keys = Object.keys(themes || {});
    return [prodKey, ...keys.filter((k) => k !== prodKey)];
  }, [prodKey]);

  const initialNameRaw = (import.meta.env.VITE_THEME as string) || prodKey;
  const initialName =
    initialNameRaw === "dark" || initialNameRaw === "light" ? prodKey : initialNameRaw;
  const [themeName, setThemeName] = useState<string>(initialName);

  // Local (non-production) overrides only
  const [overrides, setOverrides] = useState<Record<string, ThemeRecord>>(() =>
    readLS(STORAGE_KEYS.OVERRIDES, {}),
  );

  // Server-backed production history
  const [prodHistory, setProdHistory] = useState<HistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Revert preview actions modal
  const [revertActionOpen, setRevertActionOpen] = useState(false);
  
  // Session-only production override so promoted theme shows immediately
  const [productionOverride, setProductionOverride] = useState<ThemeRecord | null>(null);

  // ⬇️ Upload Theme feedback + file input ref (TOP-LEVEL hooks)
  const [uploadMsg, setUploadMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Editor modal
  const [editorOpen, setEditorOpen] = useState(false);

  const [minimized, setMinimized] = useState(false);
  const [editingTheme, setEditingTheme] = useState<ThemeRecord | null>(null);
  const [originalAtOpen, setOriginalAtOpen] = useState<ThemeRecord | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Migration (promotion) modal
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoTag, setPromoTag] = useState("");
  const [promoConfirm, setPromoConfirm] = useState("");
  const [promoting, setPromoting] = useState(false);


  // Revert (history) modal
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertMinimized, setRevertMinimized] = useState(false);
  const [revertSelectedIdx, setRevertSelectedIdx] = useState<number | null>(null);
  const [revertPreviewTheme, setRevertPreviewTheme] = useState<ThemeRecord | null>(null);
  const [factoryItem, setFactoryItem] = useState<HistoryItem | null>(null);


  // Drag is attached ONLY to the title text
  const modalRef = useRef<HTMLDivElement | null>(null);
  const dragState = useRef<{ dragging: boolean; startX: number; startY: number; origLeft: number; origTop: number } | null>(null);

  // Unified list for Revert UI: pinned factories first, then server history
  const combinedHistory = React.useMemo(() => {
    const pinned: { tag: string; at: string; theme: any; _pinned: true }[] = [];
  
    // Production as a factory item
    const prodBase = currentProductionBase();
    if (prodBase) pinned.push({
      tag: "Factory setting Production",
      at: "1970-01-01T00:00:00.000Z",
      theme: prodBase,
      _pinned: true,
    });
  
    // Seasonal factories
    const fSummer = getFactoryForTheme("summer");
    const fSpring = getFactoryForTheme("spring");
    const fFall   = getFactoryForTheme("fall");
    const fWinter = getFactoryForTheme("winter");
    if (fSummer) pinned.push({ tag: "Factory setting Summer", at: "1970-01-01T00:00:01.000Z", theme: fSummer, _pinned: true });
    if (fSpring) pinned.push({ tag: "Factory setting Spring", at: "1970-01-01T00:00:02.000Z", theme: fSpring, _pinned: true });
    if (fFall)   pinned.push({ tag: "Factory setting Fall",   at: "1970-01-01T00:00:03.000Z", theme: fFall,   _pinned: true });
    if (fWinter) pinned.push({ tag: "Factory setting Winter", at: "1970-01-01T00:00:04.000Z", theme: fWinter, _pinned: true });
  
    return [...pinned, ...prodHistory.slice(0, 10)];
  }, [prodHistory]);


  // Load server-backed history on mount + seed pinned Factory Setting once (immutable)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingHistory(true);
      try {
        // 1) fetch server history and factory
        let { items, factory } = await apiGetHistory();
  
        // 2) If factory is missing, seed it from current production (once)
        if (!factory) {
          const base = currentProductionBase();
          if (base) {
            await apiSeedFactory(base, "Factory Setting");
            const refreshed = await apiGetHistory();
            items = refreshed.items;
            factory = refreshed.factory;
          }
        }
  
        if (!alive) return;
        setFactoryItem(factory || null);
        setProdHistory(Array.isArray(items) ? items.slice(0, 10) : []);
        
        // NEW: drive Production view from latest server promotion/rollback
        const latest = (Array.isArray(items) && items[0] && items[0].theme) ? items[0].theme : null;
        if (latest && isValidThemeObject(latest)) {
          setProductionOverride(clone(latest));
          if (themeName === prodKey) {
            try { applyThemeVars(latest); } catch {}
          }
        }
      } catch (e) {
        console.error("[ThemePreview] history load failed:", e);
        if (!alive) return;
        setFactoryItem(null);
        setProdHistory([]);
      } finally {
        if (alive) setLoadingHistory(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  /** Figure out base production (file) theme */
  function currentProductionBase(): ThemeRecord | null {
    const base = resolveProductionTheme();
    return base && isValidThemeObject(base) ? clone(base) : null;
  }
  /** Effective theme for current selection (merge over production so partial themes work) */
  const effectiveTheme = useMemo(() => {
    // 1) Start from current production base (or override if on production)
    const productionBase: ThemeRecord | null =
      themeName === prodKey && productionOverride
        ? clone(productionOverride)
        : currentProductionBase();
  
    if (!productionBase) return null;
  
    let base = clone(productionBase);
  
    // 2) If a non-production theme is selected, layer its file values over production
    if (themeName !== prodKey && (themes as any)[themeName]) {
      base = { ...base, ...clone((themes as any)[themeName]) };
    }
  
    // 3) If local preview overrides exist for that non-production theme, layer them too
    if (themeName !== prodKey) {
      const ov = overrides?.[themeName];
      if (ov && typeof ov === "object") base = { ...base, ...ov };
    }
  
    // 4) If revert preview is minimized, show that regardless of selection
    if (revertOpen && revertMinimized && revertPreviewTheme) {
      base = clone(revertPreviewTheme);
    }
  
    return base;
  }, [
    themeName,
    overrides,
    prodKey,
    revertOpen,
    revertMinimized,
    revertPreviewTheme,
    productionOverride,
  ]);

  /** Apply theme whenever it changes */
  useEffect(() => {
    if (!effectiveTheme) {
      console.warn(`[ThemePreview] Theme "${themeName}" not found — reverting to ${prodKey}`);
      setTimeout(() => setThemeName(prodKey), 0);
      return;
    }
  
    // Production must be fully valid; non-production can be partial because it's merged over production.
    if (themeName === prodKey && !isValidThemeObject(effectiveTheme)) {
      console.warn(`[ThemePreview] Production theme invalid — falling back to production base`);
      const safe = currentProductionBase();
      if (safe) applyThemeVars(safe);
      return;
    }
  
    try { loadTheme(themeName); } catch {}
    applyThemeVars(effectiveTheme);
  }, [effectiveTheme, themeName, prodKey]);

  /* ---------- Editor Modal ---------- */

  function openEditor() {
    const eff = effectiveTheme;
    if (!eff) return;
  
    // Start from the effective theme, then hydrate any missing fields
    // from the *actual* CSS variables currently applied to the page.
    // This makes the editor’s swatches/pickers match what you’re seeing.
    const hydrated = hydrateThemeFromCSS(clone(eff));
  
    setOriginalAtOpen(clone(hydrated));
    setEditingTheme(hydrated);
    setUnsavedChanges(false);
    setEditorOpen(true);
    setMinimized(false);
  
    // Make sure the modal opens in a good spot/size
    requestAnimationFrame(() => {
      const m = modalRef.current;
      if (!m) return;
      m.style.left = "50%";
      m.style.top = "50%";
      m.style.transform = "translate(-50%, -50%)";
      m.style.width = "min(50vw, 900px)";
      m.style.height = "min(50vh, 650px)";
    });
  }

  function tryCloseEditor() {
    if (unsavedChanges && !confirm("You have unsaved changes. Close and discard them?")) return;
    setEditorOpen(false);
    setEditingTheme(null);
    setOriginalAtOpen(null);
    setUnsavedChanges(false);
    setMinimized(false);
  }
  function minimizeEditor() { setMinimized(true); }
  function restoreEditor() { setMinimized(false); }

  function updateField(key: FieldKey, value: any) {
    if (!editingTheme) return;
    setEditingTheme((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [key]: value };
    
      // Always reapply full theme vars so every field is reflected immediately
      try {
        applyThemeVars(next);
      } catch (e) {
        console.warn("applyThemeVars failed", e);
      }
    
      setUnsavedChanges(JSON.stringify(next) !== JSON.stringify(originalAtOpen));
      return next;
    });
  }
  function revertField(key: FieldKey) {
    if (!editingTheme || !originalAtOpen) return;
    const originalVal = originalAtOpen[key];
    setEditingTheme((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [key]: originalVal };
      applyThemeVars(next);
      setUnsavedChanges(JSON.stringify(next) !== JSON.stringify(originalAtOpen));
      return next;
    });
  }
  function revertToOriginal() {
    if (!originalAtOpen) return;
    setEditingTheme(clone(originalAtOpen));
    applyThemeVars(originalAtOpen);
    setUnsavedChanges(false);
  }
  /** Save preview override (non-production only, local only) */
  function overrideTheme() {
    if (!editingTheme) return;
    if (themeName === prodKey) return; // prod is view-only
    if (!isValidThemeObject(editingTheme)) {
      alert("Edited theme is missing required fields or invalid.");
      return;
    }
    const next = { ...(overrides || {}) };
    next[themeName] = clone(editingTheme);
    setOverrides(next);
    writeLS(STORAGE_KEYS.OVERRIDES, next);
    setUnsavedChanges(false);
    applyThemeVars(editingTheme);
    setOriginalAtOpen(clone(editingTheme));
    alert("Theme override saved locally (preview). Use 'Download for later' to export JSON.");
  }
  function downloadForLater() {
    if (!editingTheme) return;
    downloadJSON(`${themeName}-theme.json`, editingTheme);
  }

  function revertToFactoryForCurrentTheme() {
    // Only for seasonal themes
    const key = String(themeName || "").toLowerCase();
    if (!["summer","spring","fall","winter"].includes(key)) return;
  
    const factory = getFactoryForTheme(key);
    if (!factory) return;
  
    // Load the exact seasonal factory (NOT production)
    const snapshot = clone(factory);
    setEditingTheme(snapshot);
    applyThemeVars(snapshot);
    setUnsavedChanges(true);
  }


  /* ---------- Migration (Production) ---------- */

  function openMigrate() {
    setPromoTag("");
    setPromoConfirm("");
    setPromoOpen(true);
  }

  async function onUploadThemeFile(file: File) {
    // Check file format first
    const isJson = file.type === "application/json" || file.name.toLowerCase().endsWith(".json");
    if (!isJson) {
      setUploadMsg({ ok: false, text: "File can not be uploaded" });
      return;
    }
  
    try {
      const txt = await file.text();
      const parsed = JSON.parse(txt);
  
      if (!parsed || typeof parsed !== "object" || !editingTheme) {
        setUploadMsg({ ok: false, text: "File can not be uploaded" });
        return;
      }
  
      // Accept only known keys and validate types
      const allowed = new Set(FIELDS.map(f => f.key));
      const nextPatch: Record<string, any> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (!allowed.has(k)) continue;
        const def = FIELDS.find(f => f.key === k)!;
        if (def.type === "number") {
          const n = Number(v);
          if (!Number.isNaN(n)) nextPatch[k] = n;
        } else if (typeof v === "string" && v.trim() !== "") {
          nextPatch[k] = v;
        }
      }
  
      if (Object.keys(nextPatch).length === 0) {
        setUploadMsg({ ok: false, text: "File can not be uploaded" });
        return;
      }
  
      const next = { ...editingTheme, ...nextPatch };
      setEditingTheme(next);
      applyThemeVars(next);
      setUnsavedChanges(true);
      setUploadMsg({ ok: true, text: "File uploaded successfully" });
    } catch {
      setUploadMsg({ ok: false, text: "File can not be uploaded" });
    }
  }
  

  async function promoteToProduction(themeToPromote: ThemeRecord, tag: string) {
    if (!isValidThemeObject(themeToPromote)) {
      alert("Cannot promote: theme object invalid.");
      return;
    }
  
    if (promoting) return;
    setPromoting(true);
    try {
      const ok = await apiPostPromotion(tag, themeToPromote);
      if (!ok) return;
  
      // Refresh server-backed history so the list shows your latest promotion
      setLoadingHistory(true);
      const { items, factory } = await apiGetHistory();
      setProdHistory(Array.isArray(items) ? items.slice(0, 10) : []);
      setFactoryItem(factory || null);
      setLoadingHistory(false);
  
      // Reflect immediately in UI as current production for this session
      setProductionOverride(clone(themeToPromote));
      globalSetProductionOverride(clone(themeToPromote)); // NEW: update global so homepage sees it too
      
      if (themeName === prodKey) applyThemeVars(themeToPromote);
      try { window.dispatchEvent(new CustomEvent("theme:refresh")); } catch {}

      setPromoOpen(false);
      alert("Promoted to Production (server).");
    } catch (e: any) {
      alert(`Failed to promote: ${e?.message || String(e)}`);
    } finally {
      setPromoting(false);
    }
  }


  /* ---------- Revert to Earlier Production ---------- */

  function openRevert() {
    setRevertSelectedIdx(null);
    setRevertPreviewTheme(null);
    setRevertOpen(true);
    setRevertMinimized(false);
  }
  function closeRevert() {
    setRevertOpen(false);
    setRevertMinimized(false);
    setRevertSelectedIdx(null);
    setRevertPreviewTheme(null);
  }
  function previewRevertSelection() {
    if (revertSelectedIdx == null) return;
    if (revertSelectedIdx < 0 || revertSelectedIdx >= combinedHistory.length) return;
  
    const sel = combinedHistory[revertSelectedIdx];
    if (!sel || !sel.theme) return;
  
    const snap = clone(sel.theme);
    setRevertPreviewTheme(snap);
    applyThemeVars(snap);
    setRevertMinimized(true);
  }
  async function promoteReverted(skipConfirm?: boolean) {
    if (revertSelectedIdx == null || revertSelectedIdx < 0 || revertSelectedIdx >= combinedHistory.length) {
      alert("Select an item first.");
      return;
    }
  
    const sel = combinedHistory[revertSelectedIdx];
    const themeToPromote = sel?.theme;
    if (!themeToPromote || typeof themeToPromote !== "object") {
      alert("Selected entry has no theme payload.");
      return;
    }
  
    if (!skipConfirm) {
      const c = window.prompt("Type CONFIRM to promote this historical production theme:");
      if (c !== "CONFIRM") return;
    }
  
    // Tag as Rollback-<FromTag>-<date>-<time>
    const fromLabel = sel.tag || "Unknown";
    const when = new Date();
    const stamp = `${when.toLocaleDateString()}-${when.toLocaleTimeString()}`;
    const tag = `Rollback-${fromLabel}-${stamp}`;
  
    const ok = await apiPostPromotion(tag, themeToPromote);
    if (!ok) return;
  
    setLoadingHistory(true);
    const { items, factory } = await apiGetHistory();
    setProdHistory(Array.isArray(items) ? items.slice(0, 10) : []);
    setFactoryItem(factory || null);
    setLoadingHistory(false);
  
    // Reflect immediately if Production is selected
    if (themeName === getProductionKey()) applyThemeVars(themeToPromote);
    
    // Also update the global production theme so homepage picks it up
    globalSetProductionOverride(clone(themeToPromote));
    
    setRevertOpen(false);
    setRevertMinimized(false);
    alert("Historical theme promoted (server).");
  }

  /* ---------- Render ---------- */

  const isProductionSelected = themeName === prodKey;
  // Hide control panel whenever revert flow is open (even when minimized for preview)
  const anyBlocking = editorOpen || minimized || promoOpen || revertOpen;

  const showControlPanel = !anyBlocking;

  return (
    <div className="theme-preview min-h-screen bg-background text-foreground" style={{ minHeight: "100vh" }}>
      {/* Control Panel — hidden if any modal is active/visible */}
      {showControlPanel && (
        <div
          style={{
            position: "fixed",
            right: 16,
            top: 80,
            zIndex: 9999,
            background: "#fff",
            border: "1px solid rgba(0,0,0,0.06)",
            padding: 12,
            borderRadius: 10,
            boxShadow: "0 10px 30px rgba(2,6,23,0.08)",
            width: 300,
            fontFamily: "Inter, Arial, sans-serif",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Theme Preview</div>

          <select
            aria-label="Select theme"
            value={themeName}
            onChange={(e) => setThemeName(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid rgba(0,0,0,0.08)" }}
          >
            {themeKeys.map((k) => (
              <option key={k} value={k}>
                {k === prodKey ? "production" : k}
              </option>
            ))}
          </select>

          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              onClick={openEditor}
              style={{
                flex: 1,
                padding: "8px 10px",
                borderRadius: 6,
                background: "#0b69ff",
                color: "white",
                border: "none",
              }}
            >
              {isProductionSelected ? "View" : "Edit"}
            </button>

            {/* Light orange as requested; disabled on production */}
            <button
              onClick={() => {
                if (isProductionSelected) return; // guard
                openMigrate();
              }}
              disabled={isProductionSelected}
              style={{
                padding: "8px 10px",
                borderRadius: 6,
                border: "1px solid rgba(255,140,43,0.3)",
                background: isProductionSelected ? "#FFE8D1" : "#FFD7B0", // lighter when disabled
                color: "#7a3e00",
                fontWeight: 600,
                cursor: isProductionSelected ? "not-allowed" : "pointer",
                opacity: isProductionSelected ? 0.6 : 1,
              }}
              title={isProductionSelected ? "Already on Production theme" : "Promote current theme to Production"}
            >
              Migrate to Production
            </button>
          </div>

          <div style={{ marginTop: 10 }}>
            <button
              onClick={openRevert}
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 6,
                background: "#0b69ff",
                color: "white",
                border: "none",
              }}
              title="Preview and re-promote an earlier production theme (server history)"
            >
              Revert To Earlier Production Themes
            </button>
          </div>

          <div style={{ marginTop: 8, fontSize: 12, color: "#555" }}>
            <div>
              Applied:{" "}
              <strong style={{ textTransform: "capitalize" }}>
                {themeName === prodKey ? "production" : themeName}
              </strong>
            </div>
            <div style={{ marginTop: 6, color: "#777" }}>
              Edits to non-production themes are stored locally for preview. Use “Download for later” to export JSON.
            </div>
            {loadingHistory ? (
              <div style={{ marginTop: 8, color: "#777" }}>Loading production history…</div>
            ) : (
              prodHistory.length > 0 && (
                <div style={{ marginTop: 8, color: "#555" }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Recent Production Promotions</div>
                  {(() => {
                    const factory = prodHistory.find((h) => h.tag === "Factory Setting") || null;
                    const others  = prodHistory.filter((h) => h.tag !== "Factory Setting").slice(0, 10);
                    return (
                      <ul style={{ margin: 0, padding: 0, listStyle: "none", maxHeight: 220, overflow: "auto" }}>
                        {factory && (
                          <li
                            key={`factory-${factory.at}`}
                            style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px dotted #eee" }}
                          >
                            <div><strong>Factory Setting</strong></div>
                            <div style={{ color: "#777" }}>{new Date(factory.at).toLocaleString()}</div>
                          </li>
                        )}
                        {others.map((h, i) => (
                          <li
                            key={`${h.at}-${i}`}
                            style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px dotted #eee" }}
                          >
                            <div><strong>{h.tag || "(no tag)"}</strong></div>
                            <div style={{ color: "#777" }}>{new Date(h.at).toLocaleString()}</div>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Editor (View/Edit) */}
      {editorOpen && editingTheme && (
        <>
          {/* Minimized bar */}
          {minimized && (
            <div
              onClick={restoreEditor}
              style={{
                position: "fixed",
                right: 16,
                bottom: 16,
                zIndex: 10001,
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: 8,
                padding: "10px 14px",
                boxShadow: "0 10px 30px rgba(2,6,23,0.12)",
                cursor: "pointer",
              }}
              title="Restore editor"
            >
              {isProductionSelected ? `View Theme - Production` : `Edit Theme - ${themeName}`}
              {unsavedChanges ? " (Preview Version)" : ""}
            </div>
          )}

          {/* Editor window (draggable by title text only) */}
          {!minimized && (
            <div
              ref={modalRef}
              style={{
                position: "fixed",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10002,
                width: "min(50vw, 900px)",
                height: "min(50vh, 650px)",
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 20px 60px rgba(2,6,23,0.3)",
                display: "flex",
                flexDirection: "column",
                resize: "both",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "10px 12px",
                  borderBottom: "1px solid #eee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {/* Title = the ONLY drag handle */}
                <div
                  onMouseDown={(e) => {
                    const m = modalRef.current;
                    if (!m) return;
                    m.style.transform = "";
                    const rect = m.getBoundingClientRect();
                    dragState.current = {
                      dragging: true,
                      startX: e.clientX,
                      startY: e.clientY,
                      origLeft: rect.left,
                      origTop: rect.top,
                    };
                    const onMove = (ev: MouseEvent) => {
                      const s = dragState.current;
                      if (!s || !s.dragging) return;
                      const dx = ev.clientX - s.startX;
                      const dy = ev.clientY - s.startY;
                      m.style.left = `${s.origLeft + dx}px`;
                      m.style.top = `${s.origTop + dy}px`;
                    };
                    const onUp = () => {
                      if (dragState.current) dragState.current.dragging = false;
                      window.removeEventListener("mousemove", onMove);
                      window.removeEventListener("mouseup", onUp);
                    };
                    window.addEventListener("mousemove", onMove);
                    window.addEventListener("mouseup", onUp);
                  }}
                  style={{ fontWeight: 700, cursor: "move", userSelect: "none" }}
                  title="Drag window"
                >
                  {isProductionSelected ? `View Theme - Production` : `Edit Theme - ${themeName}`}
                  {unsavedChanges ? " (Preview Version)" : ""}
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={minimizeEditor}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 6,
                      background: "#fff",
                      border: "1px solid #ddd",
                      cursor: "pointer",
                    }}
                    title="Minimize"
                  >
                    _
                  </button>
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={tryCloseEditor}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 6,
                      background: "#eee",
                      border: "1px solid #ddd",
                      cursor: "pointer",
                    }}
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: 12, overflow: "auto", flex: 1 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                    alignItems: "start",
                  }}
                >
                  {FIELDS.map((f) => {
                    const cur = editingTheme?.[f.key] ?? "";
                    const disabled = isProductionSelected;
                    const isColor = f.type === "color";

                    return (
                      <div key={f.key} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>{f.label}</span>
                          {/* per-field rollback */}
                          <button
                            type="button"
                            onClick={() => revertField(f.key as FieldKey)}
                            style={{
                              marginLeft: 8,
                              padding: "2px 6px",
                              borderRadius: 4,
                              border: "1px solid #ddd",
                              background: "#f7f7f7",
                              cursor: disabled ? "not-allowed" : "pointer",
                              opacity: disabled ? 0.5 : 1,
                            }}
                            disabled={disabled}
                            title="Revert this field"
                          >
                            ↩
                          </button>
                        </label>

                        {/* Controls */}
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          {isColor && (
                            <>
                              {/* live swatch shows exact current value (hex/rgb/hsl/var/gradient) */}
                              <div
                                style={{
                                  width: 44,
                                  height: 36,
                                  borderRadius: 6,
                                  border: "1px solid #ddd",
                                  background: String(cur || "#ffffff"),
                                }}
                                title={String(cur)}
                              />
                              {/* free-form color/gradient text */}
                              <input
                                type="text"
                                value={String(cur)}
                                onChange={(e) => updateField(f.key as FieldKey, e.target.value)}
                                disabled={disabled}
                                style={{ flex: 1, minWidth: 160, padding: "8px 10px", borderRadius: 6, border: "1px solid #ddd" }}
                              />
                              {/* hex color picker */}
                              <input
                                type="color"
                                value={pickerValue(cur)}
                                onChange={(e) => updateField(f.key as FieldKey, e.target.value)}
                                disabled={disabled}
                                style={{ width: 44, height: 36, border: "none", background: "transparent" }}
                              />
                            </>
                          )}

                          {!isColor && f.type === "number" && (
                            <input
                              type="number"
                              min={("min" in f && typeof f.min === "number") ? f.min : undefined}
                              max={("max" in f && typeof f.max === "number") ? f.max : undefined}
                              step={("step" in f && typeof f.step === "number") ? f.step : 1}
                              value={String(cur ?? "")}
                              onChange={(e) => {
                                const n = e.target.value === "" ? "" : Number(e.target.value);
                                updateField(f.key as FieldKey, n === "" || Number.isNaN(n) ? "" : n);
                              }}
                              disabled={disabled}
                              style={{ flex: 1, minWidth: 160, padding: "8px 10px", borderRadius: 6, border: "1px solid #ddd" }}
                            />
                          )}

                          {!isColor && f.type === "range" && (
                            <>
                              <input
                                type="range"
                                min={("min" in f && typeof f.min === "number") ? f.min : 0}
                                max={("max" in f && typeof f.max === "number") ? f.max : 1}
                                step={("step" in f && typeof f.step === "number") ? f.step : 0.05}
                                value={Number(cur ?? 0)}
                                onChange={(e) => updateField(f.key as FieldKey, Number(e.target.value))}
                                disabled={disabled}
                                style={{ flex: 1 }}
                              />
                              <input
                                type="number"
                                min={("min" in f && typeof f.min === "number") ? f.min : 0}
                                max={("max" in f && typeof f.max === "number") ? f.max : 1}
                                step={("step" in f && typeof f.step === "number") ? f.step : 0.05}
                                value={String(cur ?? 0)}
                                onChange={(e) => updateField(f.key as FieldKey, Number(e.target.value))}
                                disabled={disabled}
                                style={{ width: 80, padding: "8px 10px", borderRadius: 6, border: "1px solid #ddd" }}
                              />
                            </>
                          )}

                          {!isColor && f.type === "select" && Array.isArray((f as any).options) && (
                            <select
                              value={String(cur || (f as any).options?.[0] || "")}
                              onChange={(e) => updateField(f.key as FieldKey, e.target.value)}
                              disabled={disabled}
                              style={{ flex: 1, minWidth: 160, padding: "8px 10px", borderRadius: 6, border: "1px solid #ddd" }}
                            >
                              {(f as any).options.map((opt: string) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          )}

                          {!isColor && f.type === "file" && (
                            <>
                              {/* URL input */}
                              <input
                                type="text"
                                placeholder="https://… or data:image/svg+xml;base64,…"
                                value={String(cur ?? "")}
                                onChange={(e) => updateField(f.key as FieldKey, e.target.value)}
                                disabled={disabled}
                                style={{ flex: 1, minWidth: 200, padding: "8px 10px", borderRadius: 6, border: "1px solid #ddd" }}
                              />
                              {/* File uploader -> data URL */}
                              <input
                                type="file"
                                accept=".png,.svg,.jpg,.jpeg,image/png,image/svg+xml,image/jpeg"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const reader = new FileReader();
                                  reader.onload = () => {
                                    if (typeof reader.result === "string") {
                                      updateField(f.key as FieldKey, reader.result);
                                    }
                                  };
                                  reader.readAsDataURL(file);
                                  e.currentTarget.value = "";
                                }}
                                disabled={disabled}
                              />
                            </>
                          )}
                        </div>

                        <div style={{ fontSize: 12, color: "#666", lineHeight: 1.3 }}>
                          <div><em>Key:</em> <code>{f.key}</code></div>
                          {("controls" in f) && (f as any).controls && (
                            <div><em>Controls:</em> {(f as any).controls}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Footer actions */}
              <div
                style={{
                  padding: 12,
                  borderTop: "1px solid #eee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                {/* Left group: Override + Upload */}
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={overrideTheme}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 8,
                      background: isProductionSelected ? "#ffd7b0" : "#ff8c2b",
                      color: "white",
                      border: "none",
                      cursor: isProductionSelected ? "not-allowed" : "pointer",
                    }}
                    disabled={isProductionSelected}
                    title={isProductionSelected ? "Production is view-only" : "Save preview override for this theme (local only)"}
                  >
                    Override Theme
                  </button>
              
                  {/* Upload Theme (JSON) — disabled on production */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProductionSelected}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 8,
                      background: isProductionSelected ? "#c7c9cf" : "#6b7280",
                      color: "white",
                      border: "none",
                      cursor: isProductionSelected ? "not-allowed" : "pointer",
                      opacity: isProductionSelected ? 0.6 : 1,
                    }}
                    title={isProductionSelected ? "Production is view-only" : "Upload a theme JSON (merges into the current editor session)"}
                  >
                    Upload Theme
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/json,.json"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onUploadThemeFile(f);
                      e.currentTarget.value = "";
                    }}
                  />
                </div>
              
                {/* Center: Revert to Original (blue) */}
                <button
                  onClick={revertToOriginal}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: unsavedChanges ? "#0b69ff" : "#cbd5e1",
                    color: "white",
                    border: "none",
                    cursor: unsavedChanges ? "pointer" : "not-allowed",
                    minWidth: 180,
                  }}
                  disabled={!unsavedChanges}
                  title="Revert editor to values from when you opened it"
                >
                  Revert to Original
                </button>

                {/* Right group: Download + Revert to Factory (for seasonal themes only) */}
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={downloadForLater}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 8,
                      background: "#0bbf6e",
                      color: "white",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Download for later
                  </button>
              
                  {["summer","spring","fall","winter"].includes(themeName) && (
                    <button
                      onClick={revertToFactoryForCurrentTheme}
                      style={{
                        padding: "10px 14px",
                        borderRadius: 8,
                        background: "#475569",
                        color: "white",
                        border: "none",
                        cursor: "pointer",
                      }}
                      title={`Revert this ${themeName} theme to its Factory setting`}
                    >
                      Revert to Factory setting {themeName}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Migrate to Production Modal */}
      {promoOpen && effectiveTheme && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 11000,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPromoOpen(false);
          }}
        >
          <div
            style={{
              width: 520,
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 20px 60px rgba(2,6,23,0.3)",
              padding: 16,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Migrate to Production</div>
            <div style={{ fontSize: 14, color: "#555", marginBottom: 8 }}>
              Add a tag/comment for this promotion (e.g., “Valentine palette v2”).
            </div>
            <input
              value={promoTag}
              onChange={(e) => setPromoTag(e.target.value)}
              placeholder="Enter tag/comment"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ddd", marginBottom: 12 }}
            />
            <div style={{ fontSize: 14, color: "#555", marginBottom: 8 }}>
              Type <code>CONFIRM</code> to enable Promote:
            </div>
            <input
              value={promoConfirm}
              onChange={(e) => setPromoConfirm(e.target.value)}
              placeholder="CONFIRM"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ddd", marginBottom: 16 }}
            />

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setPromoOpen(false)}
                style={{ padding: "8px 10px", borderRadius: 6, background: "#eee", border: "1px solid #ddd" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const canPromote = promoTag.trim().length > 0 && promoConfirm === "CONFIRM";
                  if (!canPromote || promoting) return;
                  await promoteToProduction(effectiveTheme, promoTag || "");
                }}
                disabled={promoting || !(promoTag.trim().length > 0 && promoConfirm === "CONFIRM")}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  background:
                    promoTag.trim().length > 0 && promoConfirm === "CONFIRM" ? "#0b69ff" : "#a7c0ff",
                  color: "#fff",
                  border: "none",
                  cursor:
                    promoTag.trim().length > 0 && promoConfirm === "CONFIRM" && !promoting
                      ? "pointer"
                      : "not-allowed",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 110,
                  justifyContent: "center",
                }}
                title="Promote to production (server)"
              >
                {promoting ? "Promoting…" : "Promote"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revert (history) Modal — hidden overlay when minimized */}
      {revertOpen && !revertMinimized && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 12000,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeRevert();
          }}
        >
          <div
            style={{
              width: 760,
              maxHeight: "85vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 20px 60px rgba(2,6,23,0.3)",
              padding: 16,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Revert To Earlier Production Themes</div>

            {loadingHistory ? (
              <div style={{ color: "#666", marginBottom: 12 }}>Loading history…</div>
            ) : combinedHistory.length === 0 ? (
              <div style={{ color: "#666", marginBottom: 12 }}>No production entries yet (Factory settings available).</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
                <thead>
                  <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                    <th style={{ padding: 8 }}>#</th>
                    <th style={{ padding: 8 }}>Select</th>
                    <th style={{ padding: 8 }}>Tag</th>
                    <th style={{ padding: 8 }}>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Count pinned (Factory) rows so numbering starts AFTER them
                    const pinnedCount = combinedHistory.filter((h: any) => h._pinned === true).length;
                
                    // Determine the active Production theme (override if present, else base)
                    const activeTheme = productionOverride ? clone(productionOverride) : currentProductionBase();
                
                    // Simple deep-equality for themes
                    const isSameTheme = (a: any, b: any) => {
                      if (!a || !b) return false;
                      try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
                    };
                
                    return (
                      <>
                        {combinedHistory.map((h: any, idx: number) => {
                          const isPinned = h._pinned === true;
                          const ts = h.at ? new Date(h.at).toLocaleString() : "";
                
                          // numbering: leave blank for pinned rows
                          const displayNumber = isPinned ? "" : (idx - pinnedCount + 1);
                
                          // highlight and protect the active production theme
                          const isActive = isSameTheme(h?.theme, activeTheme);
                
                          return (
                            <tr
                              key={`${h.tag}-${idx}`}
                              style={{
                                borderBottom: "1px solid #f3f3f3",
                                background: isActive ? "#FFF6E5" : undefined, // subtle highlight for active
                              }}
                              title={isActive ? "Currently active on Production" : undefined}
                            >
                              {/* # (blank for pinned) */}
                              <td style={{ padding: 8, width: 24 }}>{displayNumber}</td>
                
                              {/* Select (radio) */}
                              <td style={{ padding: 8, width: 60 }}>
                                <input
                                  type="radio"
                                  name="revertSel"
                                  checked={revertSelectedIdx === idx}
                                  onChange={() => setRevertSelectedIdx(idx)}
                                />
                              </td>
                
                              {/* Tag */}
                              <td style={{ padding: 8 }}>
                                {h.tag || "(no tag)"}
                                {isPinned && (
                                  <span style={{ marginLeft: 6, fontSize: 12, color: "#64748b" }}>
                                    (Factory)
                                  </span>
                                )}
                                {isActive && (
                                  <span style={{ marginLeft: 6, fontSize: 12, color: "#b45309", fontWeight: 600 }}>
                                    (Active)
                                  </span>
                                )}
                              </td>
                
                              {/* Timestamp */}
                              <td style={{ padding: 8 }}>{ts}</td>
                
                              {/* Delete button — only for NON-pinned and NOT active */}
                              {!isPinned && (
                                <td style={{ padding: 8 }}>
                                  <button
                                    onClick={async () => {
                                      if (isActive) return; // cannot delete active
                                      const c = window.prompt(`Type CONFIRM to delete "${h.tag || "(no tag)"}" @ ${ts}`);
                                      if (c !== "CONFIRM") { alert("Try Again"); return; }
                                      if (!h.id) { alert("Missing id for this history item."); return; }
                                      const ok = await apiDeleteHistory(h.id);
                                      if (!ok) return;
                                      setLoadingHistory(true);
                                      const { items, factory } = await apiGetHistory();
                                      setProdHistory(Array.isArray(items) ? items.slice(0, 10) : []);
                                      setFactoryItem(factory || null);
                                      setLoadingHistory(false);
                                    }}
                                    disabled={isActive}
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: 6,
                                      background: isActive ? "#fca5a5" : "#ef4444", // lighter when disabled
                                      color: "#fff",
                                      border: "none",
                                      cursor: isActive ? "not-allowed" : "pointer",
                                      opacity: isActive ? 0.6 : 1,
                                    }}
                                    title={isActive ? "Cannot delete the active production theme" : "Delete this history entry"}
                                  >
                                    Delete
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={previewRevertSelection}
                  disabled={revertSelectedIdx == null}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: revertSelectedIdx != null ? "#0b69ff" : "#a7c0ff",
                    color: "#fff",
                    border: "none",
                    cursor: revertSelectedIdx != null ? "pointer" : "not-allowed",
                  }}
                  title="Preview selected production snapshot and prepare to promote"
                >
                  Preview and Promote
                </button>
              </div>

              <button
                onClick={closeRevert}
                style={{ padding: "8px 10px", borderRadius: 6, background: "#eee", border: "1px solid #ddd" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revert minimized bar (no overlay; site fully visible) */}
      {revertOpen && revertMinimized && (
        <div
          onClick={() => setRevertActionOpen(true)}
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 12001,
            background: "#fff",
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: 8,
            padding: "10px 14px",
            boxShadow: "0 10px 30px rgba(2,6,23,0.12)",
            cursor: "pointer",
          }}
          title="Restore Revert window"
        >
          Revert To Earlier Production Themes — Previewing…
        </div>
      )}


      {/* Upload feedback dialog */}
      {uploadMsg && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 13000,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setUploadMsg(null);
          }}
        >
          <div
            style={{
              width: 360,
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 20px 60px rgba(2,6,23,0.3)",
              padding: 16,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, marginBottom: 10 }}>
              {uploadMsg.ok ? "Upload successful" : "Upload failed - Try again"}
            </div>
            <div style={{ fontSize: 14, color: "#555", marginBottom: 12 }}>{uploadMsg.text}</div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setUploadMsg(null)}
                style={{ padding: "8px 14px", borderRadius: 6, background: "#0b69ff", color: "#fff", border: "none" }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Revert preview action modal */}
      {revertActionOpen && revertOpen && revertMinimized && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 12002,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setRevertActionOpen(false);
          }}
        >
          <div
            style={{
              width: 420,
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 20px 60px rgba(2,6,23,0.3)",
              padding: 16,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Ready to promote to Production?</div>
            <div style={{ fontSize: 14, color: "#555", marginBottom: 12 }}>
              You’re previewing a historical Production theme.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  // reopen the full revert window to choose another snapshot
                  setRevertActionOpen(false);
                  setRevertMinimized(false);
                }}
                style={{ padding: "8px 10px", borderRadius: 6, background: "#eee", border: "1px solid #ddd" }}
              >
                Select Other Preview
              </button>
              <button
                onClick={async () => {
                  await promoteReverted(); // will prompt for CONFIRM
                  setRevertActionOpen(false);
                }}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  background: "#ff8c2b",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  minWidth: 110,
                }}
                title="Promote the previewed snapshot (server)"
              >
                Promote
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full homepage preview below */}
      <Header />
      <Hero />
      <About />
      <Gallery />
      <Awards />
      <Pricing />
      <Contact />
      <Footer />
    </div>
  );
}

export default ThemePreview;

// src/theme/productionTheme.ts
import type { Theme } from "./theme";

export const productionTheme: Theme = {
  background: "hsl(350 30% 98%)",
  headerBg: "linear-gradient(180deg, hsl(350 30% 98%) 0%, hsl(350 20% 94%) 100%)",
  headerTransparency: 0.6,
  fontH1: "hsl(350 70% 70%)",
  fontH2: "hsl(350 70% 70%)",
  fontH3: "hsl(350 70% 70%)",
  fontBody: "hsl(0 0% 10%)",
  buttonBg: "hsl(350 70% 70%)",
  buttonText: "hsl(0 0% 100%)",
  buttonHoverBg: "hsl(350 70% 70% / 0.9)",
  tabActiveBg: "hsl(350 30% 98%)",
  tabActiveText: "hsl(0 0% 10%)",
  tabInactiveBg: "hsl(350 20% 92%)",
  tabInactiveText: "hsl(0 0% 45%)",

  // Hero defaults (original look)
  heroH1: "#ffffff",
  heroH2: "rgba(255,255,255,0.90)",
  heroBtnBg: "#e6a4a4",
  heroBtnText: "#ffffff",
  heroBtnHoverBg: "#cc8f8f",
  heroBtnHoverText: "#f5f5f5",
};

// 🔹 Keep a mutable override for live changes
let productionOverride: Theme | null = null;

/** Always return the effective production theme (base + override if present) */
export function resolveProductionTheme(): Theme {
  return productionOverride ? { ...productionTheme, ...productionOverride } : productionTheme;
}

/** Allow ThemePreview to update the global override (session only) */
export function setProductionOverride(theme: Theme | null): void {
  productionOverride = theme;
}
